import L, { PointTuple } from 'leaflet';
import { Site } from '../../../../../types';
import { useEffect, useRef } from 'react';
import { PanoMarker } from './PanoMarker';
import { renderToString } from 'react-dom/server';
import { UploadData } from '../../../../../store/uploadStore';

const PROXIMITY_THRESHOLD_METERS = 20;

const iconHtml = renderToString(<PanoMarker />);

const createIcon = (expanded = false, allPhotosUploaded = false, isUploading = false, isClusterEmpty = false) => {
  const screenAdjustedIconSize = Math.max(Math.min(Math.round(window.innerWidth / 30), 40), 24);
  const iconSize = expanded ? [screenAdjustedIconSize, screenAdjustedIconSize] : [20, 20];

  let className = 'pano-marker';

  if (isUploading) {
    className = 'pano-marker--uploading';
  } else if (allPhotosUploaded) {
    className = isClusterEmpty ? 'pano-marker--uploaded-invalid' : 'pano-marker--uploaded-valid';
  }

  return L.divIcon({
    className,
    iconSize: iconSize as PointTuple,
    html: iconHtml,
    iconAnchor: [iconSize[0] / 2, iconSize[1] / 2],
  });
};

export const useAddObservePanoramas = (site: Site, expanded: boolean, panoUpload: UploadData | undefined) => {
  const panoramasRef = useRef<L.Marker[]>([]);

  const drawPanoramas = () => {
    const photosToGenerateProgress = [...(panoUpload?.upload?.photos || []), ...(panoUpload?.queue || [])].filter(
      ({ fileCategory, coords }) => fileCategory === 'image' && coords && coords.lon && coords.lat,
    );

    const allPhotosUploaded =
      !!panoUpload?.upload &&
      panoUpload.overview.photosCount.all > 0 &&
      panoUpload.overview.photosCount.uploaded === panoUpload.overview.photosCount.all;

    const isUploading = !!panoUpload?.upload && panoUpload.queue.length > 0;

    panoramasRef.current.forEach((marker) => {
      const markerCoords = marker.getLatLng();

      const nearbyPhotos = photosToGenerateProgress.filter(({ coords }) => {
        if (!coords) return false;

        const photoLatLng = L.latLng(coords.lat, coords.lon);
        return markerCoords.distanceTo(photoLatLng) <= PROXIMITY_THRESHOLD_METERS;
      });

      const isClusterEmpty = !!panoUpload?.upload && nearbyPhotos.length === 0;

      marker.setIcon(createIcon(expanded, allPhotosUploaded, isUploading, isClusterEmpty));
    });
  };

  const addPanoramas = (map: L.Map) => {
    const locations = site.projectInfo.parsedPanoramaLocations;
    const splitCoordinates = locations?.map((s) => s.split(/[,;]/).map((l) => l.trim())).filter((s) => s.length >= 2);

    if (splitCoordinates) {
      for (const [index, coords] of splitCoordinates.entries()) {
        const coordsAsNumbers = coords.map((c) => parseFloat(c.replace(',', '.')));

        if (Number.isFinite(coordsAsNumbers[0]) && Number.isFinite(coordsAsNumbers[1])) {
          panoramasRef.current[index] = L.marker([coordsAsNumbers[0], coordsAsNumbers[1]], {
            icon: createIcon(),
            interactive: false,
            pane: 'popupPane',
          }).addTo(map);
        }
      }

      drawPanoramas();
    }
  };

  useEffect(() => {
    drawPanoramas();
  }, [expanded, panoUpload]);

  return {
    addPanoramas,
    drawPanoramas,
    panoramasRef,
  };
};
